import {
  ApiClient,
  ApiError,
  browserRefreshTokenStore,
  memoryRefreshTokenStore,
  type TokenPair,
} from '@repere/api-client';
import type { Locale } from '@repere/core';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { estNatif } from '../natif/pont';

export interface Profil {
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  locale: Locale;
  /**
   * Rôle du compte. Sert à savoir s'il y a un back-office à proposer — **jamais** à
   * autoriser quoi que ce soit : chaque route d'administration revérifie le rôle en base,
   * et un champ que le navigateur détient n'ouvre rien.
   *
   * Facultatif dans le type : une réponse d'API antérieure à T-058, ou un test qui ne le
   * pose pas, ne doit pas faire échouer le rendu de la coquille.
   */
  role?: 'member' | 'admin';
  /**
   * Accord donné à la lecture des justificatifs par l'IA. `null` = jamais demandé.
   *
   * Trois états, et le troisième porte tout le dispositif : tant qu'il vaut `null`, rien
   * n'a été demandé et rien ne part. L'écran des dépenses pose alors la question au premier
   * justificatif déposé.
   *
   * Facultatif dans le type, comme `role` : une réponse d'API antérieure ne doit pas casser
   * un écran qui n'en dépend pas.
   */
  receiptScanConsent?: boolean | null;
}

export interface Session {
  client: ApiClient;
  profil: Profil | null;
  /** Vrai tant que la restauration de session initiale n'a pas abouti. */
  chargement: boolean;
  connecter: (email: string, motDePasse: string) => Promise<void>;
  /** Termine une connexion passée par un fournisseur tiers, avec son code de reprise. */
  reprendreConnexion: (reprise: string) => Promise<void>;
  deconnecter: () => Promise<void>;
  /** Recharge le profil après une session ouverte hors de `connecter`. */
  rafraichirProfil: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

/**
 * Session de l'utilisateur.
 *
 * Au démarrage, l'application tente une restauration silencieuse : un parent qui rouvre
 * l'onglet ne doit pas ressaisir son mot de passe. Si la session est perdue en cours
 * d'usage — jeton révoqué, mot de passe changé ailleurs — le profil est vidé et
 * l'interface repasse à l'écran de connexion, sans message accusateur.
 */
export function FournisseurSession({
  children,
  client: clientFourni,
}: {
  children: ReactNode;
  client?: ApiClient;
}) {
  const [profil, setProfil] = useState<Profil | null>(null);
  const [chargement, setChargement] = useState(true);

  const client = useMemo(() => {
    if (clientFourni) return clientFourni;
    // Le jeton de rafraîchissement ne vit plus dans le stockage local du navigateur
    // (T-039) : l'API le pose en cookie `httpOnly`, et le client le laisse là. La coque
    // native, elle, n'a pas de bocal à cookies fiable sur un schéma personnalisé : son
    // transport reste le corps de requête, avec le stockage local pour survivre au
    // redémarrage de l'application.
    const natif = estNatif();
    return new ApiClient({
      baseUrl: import.meta.env.VITE_API_URL ?? '/api',
      refreshTokens: natif ? browserRefreshTokenStore() : memoryRefreshTokenStore(),
      trustRefreshCookie: !natif,
      onSessionLost: () => setProfil(null),
    });
  }, [clientFourni]);

  const chargerProfil = useCallback(async (): Promise<void> => {
    const p = await client.request<Profil>('/auth/me');
    setProfil(p);
  }, [client]);

  useEffect(() => {
    let annule = false;
    void (async () => {
      try {
        if (await client.restore()) {
          const p = await client.request<Profil>('/auth/me');
          if (!annule) setProfil(p);
        }
      } catch {
        // Session non restaurable : on repart de l'écran de connexion, sans bruit.
        client.forget();
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => {
      annule = true;
    };
  }, [client]);

  const connecter = useCallback(
    async (email: string, motDePasse: string): Promise<void> => {
      await client.login(email, motDePasse);
      await chargerProfil();
    },
    [client, chargerProfil],
  );

  /**
   * Termine une connexion Google.
   *
   * Le code de reprise arrive dans l'URL ; les jetons, eux, s'échangent hors de l'URL et
   * n'y apparaissent jamais — un jeton de rafraîchissement dans l'historique du navigateur
   * survivrait à la session qu'il ouvre. `skipRefresh` : cet appel **est** l'ouverture de
   * session, il n'y a aucune session à renouveler s'il échoue.
   */
  const reprendreConnexion = useCallback(
    async (reprise: string): Promise<void> => {
      const paire = await client.request<TokenPair>('/auth/complete', {
        method: 'POST',
        body: { reprise },
        skipRefresh: true,
      });
      client.adopt(paire);
      await chargerProfil();
    },
    [client, chargerProfil],
  );

  /**
   * Adopte la session ouverte par un parcours qui s'authentifie lui-même — aujourd'hui
   * l'arrivée du parent invité. Le profil est rechargé pour que l'en-tête et les gardes de
   * route voient un utilisateur connecté sans passer par un rechargement de page.
   */
  const rafraichirProfil = useCallback(async (): Promise<void> => {
    await chargerProfil();
  }, [chargerProfil]);

  const deconnecter = useCallback(async (): Promise<void> => {
    await client.logout();
    setProfil(null);
  }, [client]);

  const valeur = useMemo<Session>(
    () => ({
      client,
      profil,
      chargement,
      connecter,
      reprendreConnexion,
      deconnecter,
      rafraichirProfil,
    }),
    [client, profil, chargement, connecter, reprendreConnexion, deconnecter, rafraichirProfil],
  );

  return <SessionContext.Provider value={valeur}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession doit être utilisé dans un FournisseurSession.');
  return session;
}

/** Message lisible pour une erreur d'API, sans réécrire ce que le serveur a déjà rédigé. */
export function messageDErreur(erreur: unknown): string {
  if (erreur instanceof ApiError) return erreur.message;
  if (erreur instanceof Error && erreur.name === 'NetworkError') return erreur.message;
  return 'Une erreur est survenue. Réessayez dans un instant.';
}
